// `/studio` — what somebody has built, what has been shared with them, and the
// templates a parameter instantiates.
//
// Three sections rather than one list with an owner column, because the three
// answer different questions: "what am I working on", "what can I look at", and
// "what can I point at an experiment". A template is a dashboard carrying an
// `experiment_key`, which is exactly the set `/x/<key>` can open.
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import type { StudioDashboardSummary } from "@bi/shared/types";

import { EmptyState, ErrorState, LoadingState } from "../components/states";
import { biSourcesQuery, studioDashboardsQuery } from "../lib/queries";

export function StudioListPage() {
  const dashboards = useQuery(studioDashboardsQuery);
  const sources = useQuery(biSourcesQuery);

  if (dashboards.isPending) return <LoadingState label="Loading your dashboards…" />;
  if (dashboards.isError) {
    return <ErrorState error={dashboards.error} onRetry={() => void dashboards.refetch()} />;
  }

  const rows = dashboards.data.dashboards;
  const mine = rows.filter((d) => d.isOwner);
  const shared = rows.filter((d) => !d.isOwner);
  const templates = rows.filter((d) => d.experimentKey);

  // An empty source list with no `syncedAt` is "the studio views have not
  // shipped to this environment yet", which is a different sentence from
  // "there is nothing to build over" — and the difference decides whether
  // somebody should wait or go and write a view.
  const noSources = sources.data && sources.data.sources.length === 0;

  return (
    <div className="bi-page">
      <div className="bi-page__head">
        <h1 className="bi-page__title">Studio</h1>
        <Link to="/experiments" className="bi-header__link">
          Start from an experiment
        </Link>
        <Link to="/studio/new" className="bi-header__link">
          Blank dashboard
        </Link>
      </div>

      <p className="bi-page__sub">
        A dashboard for one experiment starts on the Experiments page: pick the experiment and
        the canvas opens already scoped to it, with a starting set of tiles you can change or
        throw away.
      </p>

      {noSources ? (
        <p className="bi-page__sub">
          {sources.data?.syncedAt
            ? "This environment has no studio views to build over yet."
            : "The studio views have not reached this environment's warehouse yet, so there is nothing to build over. They arrive with the warehouse pin."}
        </p>
      ) : null}

      <Section
        title="My dashboards"
        rows={mine}
        empty="You have not built one yet."
      />
      <Section
        title="Shared with me"
        rows={shared}
        empty="Nobody has shared a dashboard with you."
      />
      <Section
        title="Filed against an experiment"
        rows={templates}
        empty="No dashboard here is bound to an experiment yet."
        experimentLinks
      />
    </div>
  );
}

function Section({
  title,
  rows,
  empty,
  experimentLinks = false,
}: {
  title: string;
  rows: readonly StudioDashboardSummary[];
  empty: string;
  experimentLinks?: boolean;
}) {
  return (
    <section className="bi-section">
      <h2 className="bi-page__title">{title}</h2>
      {rows.length === 0 ? (
        <EmptyState title={empty} />
      ) : (
        <div className="bi-catalog-grid">
          {rows.map((row) => (
            <Link
              key={row.id}
              to="/studio/$id"
              params={{ id: row.id }}
              className="bi-catalog-card"
            >
              <span className="bi-catalog-card__title">{row.title}</span>
              <span className="bi-catalog-card__meta">
                {row.ownerEmail}
                {row.visibility === "shared" ? ", shared" : ", private"}, version {row.version}
              </span>
              {experimentLinks && row.experimentKey ? (
                <span className="bi-catalog-card__meta">
                  Filed under {row.experimentKey}. Reopen it for another experiment from the
                  bar inside it, or from that experiment's page.
                </span>
              ) : null}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
